import React from "react";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../../firebase";
import { Navbar } from "../../Components/Navbar/Navbar";
import { Footer } from "../../Components/Footer/footer";
import InelImg from "../../Assets/smart-sanitas/inel.png";
import BrataraImg from "../../Assets/smart-sanitas/bratara.jpg";
import BrelocImg from "../../Assets/smart-sanitas/breloc.png";
import CardImg from "../../Assets/smart-sanitas/card-digital.png";
import "./membri-de-sindicat.css";
import {
  DEFAULT_MEMBRI_PAGE_CONTENT,
  MEMBERS_CONTENT_DOC_PATH,
  MembriPageContent,
  mergeMembriContent,
  OrderFormContent,
  ProductItem,
  SizeTableItem,
  TextItem,
} from "./content";

type OrderFormState = {
  product: string;
  quantity: number;
  fullName: string;
  email: string;
  phone: string;
  organization: string;
  address: string;
  notes: string;
  consent: boolean;
  ringCircumference: string;
  ringSizeLabel: string;
};

const PRODUCT_IMAGE_BY_ID: Record<string, string> = {
  card: CardImg,
  inel: InelImg,
  bratara: BrataraImg,
  breloc: BrelocImg,
};

function buildInitialForm(content: OrderFormContent): OrderFormState {
  return {
    product: content.productOptions[0]?.value || "card",
    quantity: 1,
    fullName: "",
    email: "",
    phone: "",
    organization: "",
    address: "",
    notes: "",
    consent: false,
    ringCircumference: "",
    ringSizeLabel: "",
  };
}

function mmToClosestSize(mm: number, sizeTable: SizeTableItem[]): SizeTableItem | null {
  if (!sizeTable.length) {
    return null;
  }

  let best = sizeTable[0];
  let bestDiff = Math.abs(mm - best.mm);

  for (const row of sizeTable) {
    const diff = Math.abs(mm - row.mm);
    if (diff < bestDiff) {
      best = row;
      bestDiff = diff;
    }
  }

  return best;
}

function getProductImage(product: ProductItem): string {
  if (product.imageUrl) {
    return product.imageUrl;
  }

  return PRODUCT_IMAGE_BY_ID[product.id] || CardImg;
}

function RenderSteps({ items }: { items: TextItem[] }) {
  return (
    <ol className="steps">
      {items.map((item, index) => (
        <li key={`${item.title}-${index}`}>
          <span className="step-number">{index + 1}</span>
          <div>
            <h4>{item.title}</h4>
            <p>{item.description}</p>
          </div>
        </li>
      ))}
    </ol>
  );
}

function OrderForm({
  content,
  sizeTable,
}: {
  content: OrderFormContent;
  sizeTable: SizeTableItem[];
}) {
  const [form, setForm] = React.useState<OrderFormState>(() => buildInitialForm(content));
  const [sending, setSending] = React.useState(false);
  const [msg, setMsg] = React.useState<string>("");
  const [institutes, setInstitutes] = React.useState<string[]>([]);
  const [instLoading, setInstLoading] = React.useState<boolean>(true);
  const [instErr, setInstErr] = React.useState<string>("");

  React.useEffect(() => {
    setForm(buildInitialForm(content));
  }, [content]);

  React.useEffect(() => {
    (async () => {
      try {
        setInstLoading(true);
        setInstErr("");
        const snap = await getDocs(query(collection(db, "institutes"), orderBy("name")));
        const names = snap.docs
          .map((d) => {
            const data = d.data() as { name?: string };
            return data.name || "";
          })
          .filter(Boolean);
        setInstitutes(names);
      } catch (error) {
        console.error(error);
        setInstErr(content.organizationErrorPlaceholder);
      } finally {
        setInstLoading(false);
      }
    })();
  }, [content.organizationErrorPlaceholder]);

  const onChange = (
    e:
      | React.ChangeEvent<HTMLInputElement>
      | React.ChangeEvent<HTMLTextAreaElement>
      | React.ChangeEvent<HTMLSelectElement>
  ) => {
    const { name, value, type, checked } = e.target as HTMLInputElement;

    if (name === "ringCircumference") {
      const circumference = value;
      let sizeLabel = "";

      if (circumference && !Number.isNaN(parseFloat(circumference))) {
        const entry = mmToClosestSize(parseFloat(circumference), sizeTable);
        sizeLabel = entry?.label || "";
      }

      setForm((prev) => ({ ...prev, ringCircumference: circumference, ringSizeLabel: sizeLabel }));
      return;
    }

    setForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMsg("");

    if (!form.fullName || !form.email || !form.phone || !form.organization) {
      setMsg(content.requiredFieldsMessage);
      return;
    }

    if (!form.consent) {
      setMsg(content.consentRequiredMessage);
      return;
    }

    try {
      setSending(true);
      await addDoc(collection(db, "smart_orders"), {
        ...form,
        quantity: Number(form.quantity) || 1,
        status: "new",
        createdAt: serverTimestamp(),
      });

      setMsg(content.successMessage);
      setForm(buildInitialForm(content));
    } catch (error) {
      console.error(error);
      setMsg(content.errorMessage);
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="section order-form">
      <section className="section smart-guide center-block">
        <h2 className="center-title">{content.measurementTitle}</h2>
        <div className="guide-grid center-narrow">
          <div className="guide-text">
            <RenderSteps items={content.measurementSteps} />
          </div>
        </div>
      </section>

      <h2>{content.introTitle}</h2>
      <p className="order-lead">{content.introLead}</p>

      <form onSubmit={onSubmit} className="of-grid">
        <div className="of-field">
          <label>{content.productLabel}</label>
          <select name="product" value={form.product} onChange={onChange}>
            {content.productOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="of-field">
          <label>{content.ringCircumferenceLabel}</label>
          <input
            type="number"
            name="ringCircumference"
            step="0.1"
            min={45}
            max={80}
            placeholder={content.ringCircumferencePlaceholder}
            value={form.ringCircumference}
            onChange={onChange}
          />
        </div>

        <div className="of-field">
          <label>{content.ringSizeLabel}</label>
          <input
            value={form.ringSizeLabel ? `~ ${form.ringSizeLabel}` : ""}
            placeholder={content.ringSizePlaceholder}
            readOnly
          />
        </div>

        <div className="of-field wide">
          <label>{content.fullNameLabel}</label>
          <input
            name="fullName"
            placeholder={content.fullNamePlaceholder}
            value={form.fullName}
            onChange={onChange}
            required
          />
        </div>

        <div className="of-field">
          <label>{content.emailLabel}</label>
          <input
            type="email"
            name="email"
            placeholder={content.emailPlaceholder}
            value={form.email}
            onChange={onChange}
            required
          />
        </div>

        <div className="of-field">
          <label>{content.phoneLabel}</label>
          <input
            name="phone"
            placeholder={content.phonePlaceholder}
            value={form.phone}
            onChange={onChange}
            required
          />
        </div>

        <div className="of-field wide">
          <label>{content.organizationLabel}</label>

          {instLoading ? (
            <input value={content.organizationLoadingLabel} disabled />
          ) : institutes.length > 0 ? (
            <select name="organization" value={form.organization} onChange={onChange} required>
              <option value="">{content.organizationSelectPlaceholder}</option>
              {institutes.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          ) : (
            <>
              <input
                name="organization"
                placeholder={instErr ? content.organizationErrorPlaceholder : content.organizationEmptyPlaceholder}
                value={form.organization}
                onChange={onChange}
                required
              />
              {instErr && <small style={{ color: "#a00" }}>{instErr}</small>}
            </>
          )}
        </div>

        <div className="of-field wide">
          <label>{content.notesLabel}</label>
          <textarea
            name="notes"
            rows={3}
            placeholder={content.notesPlaceholder}
            value={form.notes}
            onChange={onChange}
          />
        </div>

        <label className="of-consent wide">
          <input type="checkbox" name="consent" checked={form.consent} onChange={onChange} />
          <span>{content.consentLabel}</span>
        </label>

        {msg && <p className="of-msg">{msg}</p>}

        <div className="of-actions wide">
          <button type="submit" className="btn btn-primary" disabled={sending}>
            {sending ? content.sendingLabel : content.submitLabel}
          </button>
          <a className="btn btn-ghost" href={content.detailsProductsUrl} target="_blank" rel="noreferrer">
            {content.detailsProductsLabel}
          </a>
        </div>
      </form>
    </section>
  );
}

export function MembriDeSindicatPage() {
  const [content, setContent] = React.useState<MembriPageContent>(DEFAULT_MEMBRI_PAGE_CONTENT);

  React.useEffect(() => {
    (async () => {
      try {
        const docRef = doc(db, MEMBERS_CONTENT_DOC_PATH.collection, MEMBERS_CONTENT_DOC_PATH.id);
        const snap = await getDoc(docRef);
        if (!snap.exists()) {
          return;
        }

        const data = snap.data() as Partial<MembriPageContent>;
        setContent(mergeMembriContent(DEFAULT_MEMBRI_PAGE_CONTENT, data));
      } catch (error) {
        console.error("Failed to load membri-de-sindicat content:", error);
      }
    })();
  }, []);

  return (
    <div className="page-container">
      <Navbar />

      <div className="page-content membri-page">
        <section className="hero">
          <div className="hero-inner">
            <h1>{content.hero.title}</h1>
            <p className="lead">{content.hero.lead}</p>

            <div className="hero-ctas">
              <a className="btn btn-primary" href={content.hero.primaryCtaUrl} target="_blank" rel="noreferrer">
                {content.hero.primaryCtaLabel}
              </a>
            </div>
          </div>
        </section>

        <section className="section">
          <h2>{content.benefits.title}</h2>
          <div className="benefits-grid">
            {content.benefits.items.map((item, index) => (
              <article className="card" key={`${item.title}-${index}`}>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="section smart-offer">
          <h2>{content.smartOffer.title}</h2>
          <p className="smart-lead">{content.smartOffer.lead}</p>

          <div className="products-grid">
            {content.smartOffer.products.map((product) => (
              <article className={`product-card ${product.free ? "free" : ""}`} key={product.id}>
                <div className="media">
                  <img src={getProductImage(product)} alt={product.imageAlt} />
                </div>
                <div className="body">
                  <h3>{product.title}</h3>
                  <p className="desc">{product.description}</p>

                  <div className="price">
                    {product.oldPrice ? <span className="old">{product.oldPrice}</span> : null}
                    <span className={product.free ? "new free-tag" : "new"}>{product.newPrice}</span>
                  </div>

                  <div className="split">
                    <span className={product.free ? "chip neutral" : "chip you"}>{product.chipText}</span>
                  </div>

                  <a
                    className={`btn ${product.primaryButton ? "btn-primary" : "btn-ghost"} wide`}
                    href={product.buttonUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {product.buttonLabel}
                  </a>
                </div>
              </article>
            ))}
          </div>

          <p className="footnote">{content.smartOffer.footnote}</p>
        </section>

        <section className="section">
          <OrderForm content={content.orderForm} sizeTable={content.sizeTable} />
          <h2>{content.signupSteps.title}</h2>
          <RenderSteps items={content.signupSteps.items} />

          <div className="steps-cta">
            <a className="btn btn-primary" href={content.signupSteps.primaryCtaUrl} target="_blank" rel="noreferrer">
              {content.signupSteps.primaryCtaLabel}
            </a>
            <a className="btn btn-ghost" href={content.signupSteps.secondaryCtaUrl}>
              {content.signupSteps.secondaryCtaLabel}
            </a>
          </div>
        </section>

        <section className="band">
          <p>{content.quote.text}</p>
        </section>

        <section className="section">
          <h2>{content.faq.title}</h2>
          <div className="faq">
            {content.faq.items.map((item, index) => (
              <details key={`${item.question}-${index}`}>
                <summary>{item.question}</summary>
                <p>{item.answer}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="section contact-help">
          <div className="help-card">
            <h3>{content.help.title}</h3>
            <p>{content.help.description}</p>
            <div className="hero-ctas">
              <a className="btn btn-ghost" href={content.help.contactUrl}>
                {content.help.contactLabel}
              </a>
              <a className="btn btn-primary" href={content.help.primaryCtaUrl} target="_blank" rel="noreferrer">
                {content.help.primaryCtaLabel}
              </a>
            </div>
          </div>
        </section>

        <section className="section smart-guide">
          <h2>{content.usageGuide.title}</h2>

          <RenderSteps items={content.usageGuide.items} />

          <div className="steps-cta">
            <a className="btn btn-primary" href={content.usageGuide.storeButtonUrl} target="_blank" rel="noreferrer">
              {content.usageGuide.storeButtonLabel}
            </a>
            <a className="btn btn-ghost" href={content.usageGuide.appButtonUrl} target="_blank" rel="noreferrer">
              {content.usageGuide.appButtonLabel}
            </a>
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
}
